/* eslint-disable indent */
/* eslint-disable no-nested-ternary */
import React from 'react';
import Moment from 'moment';
import PropTypes from 'prop-types';
import Avatar from '@mui/material/Avatar';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import TextField from '@mui/material/TextField';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Skeleton from '@mui/material/Skeleton';
import WrongLocation from '@mui/icons-material/WrongLocation';
import LocationOn from '@mui/icons-material/LocationOn';
import AddLocation from '@mui/icons-material/AddLocation';
import Edit from '@mui/icons-material/Edit';
import LoadingButton from '@mui/lab/LoadingButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  useSelector,
  useDispatch,
} from 'react-redux';
import UserService from '@pingum/api-services/UserService';
import TagService from '@pingum/api-services/TagService';

import { Divider } from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import HorseService from '../../../../../services/HorseService';
import theme from '../../../../../utils/theme';
import { updateBarnHorse } from '../../../../../redux/barn/actions';
import {
  isUserAdmin,
  isUserOwner,
} from '../../../../../utils/tenantAuthenticationHelper';
import {
  getAllLodgingsForHorse,
  getLodgingsForHorse,
} from '../../../../../redux/horse/actions';

const LodgingsCardComponent = ({
  id,
  defaultOpen,
  border,
}) => {
  const dispatch = useDispatch();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));

  const [pageSize] = React.useState(10);
  const [sortModel] = React.useState([
    {
      field: 'createdTimestamp',
      sort: 'desc',
    },
  ]);
  const [isChangingStatus, setIsChangingStatus] = React.useState(false);
  const [openChangeStatusModal, setOpenChangeStatusModal] = React.useState(false);
  const [changeError, setChangeError] = React.useState(false);
  const [changeErrorMessage, setChangeErrorMessage] = React.useState('');
  const [note, setNote] = React.useState('');
  const [editNote, setEditNote] = React.useState('');
  const [open, setOpen] = React.useState(false);

  // Redux state
  const {
    allHorseLodgings,
    currentHorseLodging,
    isFetching,
    isFetchingAllHorseLodgings,
    isFetchingHorseLodgings,
  } = useSelector((state) => state.horse);
  const selectedBarn = useSelector((state) => state.barn.selectedBarn);
  const {
    user,
    permissions,
  } = useSelector((state) => state.user);

  React.useEffect(() => {
    if (defaultOpen) {
      setOpen(true)
    }
  }, [defaultOpen])

  React.useEffect(() => {
    setOpenChangeStatusModal(false);
    setChangeError(false);
    setOpen(false);
    setNote('');
    setChangeErrorMessage('');
    dispatch(getAllLodgingsForHorse(id, 0, pageSize, sortModel.map((s) => `&sort=${s.field},${s.sort}`)));
  }, [id])

  React.useEffect(() => {
    setEditNote((currentHorseLodging && currentHorseLodging.note) || '')
  }, [currentHorseLodging])

  const onChangeStatus = () => {
    const inactive = !currentHorseLodging.inactive;
    const changeDate = new Date();
    setIsChangingStatus(true);

    // archive or unarchive the tag id TODO do we need this if the API does it now?
    TagService.editTag(parseInt(currentHorseLodging.pingumTagId, 10), { archived: inactive }).then(() => {
      // first update the current lodging to end now
      dispatch(updateBarnHorse(currentHorseLodging.barn.id, currentHorseLodging.horse.id, { endDate: changeDate })).then(() => {
        // then add the new one with the oposite status
        HorseService.updateHorse(id, {
          lodgeHorseWithBarn: true,
          lodgingStartDate: changeDate,
          lodgingNote: note,
          lodgingInactive: inactive,
        }).then(() => {
          setIsChangingStatus(false)
          setChangeError(false);
          setOpenChangeStatusModal(false);
          setOpen(false);
          // refrseh data
          dispatch(getLodgingsForHorse(id, selectedBarn.id));
          dispatch(getAllLodgingsForHorse(id, 0, pageSize, sortModel.map((s) => `&sort=${s.field},${s.sort}`)));
        }).catch((e3) => {
          setIsChangingStatus(false)
          console.error(e3);
          setChangeError(true);
        })
      }).catch((e2) => {
        if (e2 && e2.message && e2.message.indexOf('Questri Plan') >= 0) {
          setChangeErrorMessage(e2.message)
        }
        setIsChangingStatus(false)
        console.error(e2);
        setChangeError(true);
      });
    }).catch((e1) => {
      console.error(e1);
      if (e1 && e1.message && e1.message.indexOf('active (not complete or ended) schedules') >= 0) {
        setChangeErrorMessage('There are active and scheduled tasks or calendar events for this horse. First end those scheduled tasks/calendar events or remove the horse from any that are still active.')
      } else if (e1 && e1.message && e1.message.indexOf('active (not complete or archived) events') >= 0) {
        setChangeErrorMessage('There are active tasks for this horse. First complete or archive all active tasks.')
      }
      setIsChangingStatus(false)
      setChangeError(true);
    })
  };

  const handleEditNote = () => {
    setIsChangingStatus(true);
    dispatch(updateBarnHorse(currentHorseLodging.barn.id, currentHorseLodging.horse.id, { note: editNote })).then(() => {
      setIsChangingStatus(false);
      dispatch(getLodgingsForHorse(id, selectedBarn.id));
      setOpen(false);
    })
  }

  const roles = UserService.getUserRolesForTenant(
    user,
    selectedBarn.tenant,
  );
  // eslint-disable-next-line no-unused-vars
  const isAdmin = isUserAdmin(roles) || isUserOwner(roles);

  return (
    <List aria-label="horse lodging History">{
      isFetchingAllHorseLodgings
        ? (
          <ListItemButton>
            <ListItemIcon><Skeleton variant="circular" width={40} height={40} /></ListItemIcon>
            <ListItemText
              primaryTypographyProps={{ component: 'div' }}
              primary={<Skeleton height={40} width={80} />}
              secondaryTypographyProps={{ component: 'div' }}
              secondary={<Skeleton height={20} width={200} />}
            />
          </ListItemButton>
        )
        : (
          <ListItem
            button={(permissions.owner || permissions.admin)}
            onClick={(permissions.owner || permissions.admin)
              ? () => { setOpen(!open); }
              : () => { }}
            disablePadding
            sx={border && {
              borderRadius: 12,
              border: `1px solid ${blueGrey[100]}`,
            }}
          >
            <ListItemButton>
              <ListItemIcon>
                {
                  (currentHorseLodging && currentHorseLodging.id)
                    ? (
                      <Avatar src={`https://d8adkpqcrx2wo.cloudfront.net/${currentHorseLodging.barn.tenant}.png`} alt={currentHorseLodging.barn.tenant.toUpperCase()} />
                    )
                    : (
                      <Avatar>
                        <WrongLocation />
                      </Avatar>
                    )
                }
              </ListItemIcon>
              <ListItemText
                primary={(currentHorseLodging && currentHorseLodging.id)
                  ? currentHorseLodging.barn.name
                  : `Not Boarding @ ${selectedBarn.name}`}
                secondaryTypographyProps={{ component: 'div' }}
                secondary={(currentHorseLodging && currentHorseLodging.id)

                  ? (
                    <Box
                      sx={{
                        maxHeight: 60,
                        overflow: 'auto',
                      }}
                    >
                      <Typography variant="caption" component="div">{currentHorseLodging.inactive
                        ? `Inactive Since ${Moment(currentHorseLodging.startDate).format('ll')}`
                        : `Active Since ${Moment(currentHorseLodging.startDate).format('ll')}`}
                      </Typography>
                      <Typography variant="caption" component="div" sx={{ maxWidth: 500 }}>{currentHorseLodging.note || ''}</Typography>
                    </Box>
                  )
                  : <Typography variant="caption">This horse is currently not part of your Questri organization. You can add it or request access if it is currently part of another Questri org.</Typography>}
                primaryTypographyProps={{ variant: 'h5' }}
              />
              {(permissions.owner || permissions.admin) && <Edit color="secondary" sx={{ ml: 1 }} />}
            </ListItemButton>
          </ListItem>
        )
    }
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="modal-horse-activation"
        aria-describedby="modal-horse-activation-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: mobile
              ? 400
              : 700,
            bgcolor: 'background.paper',
            pt: 2,
            px: 4,
            pb: 3,
          }}
        >
          <Typography
            id="modal-add-horse-access-title"
            variant="h6"
            component="h2"
            align="center"
            gutterBottom
          >
            Manage <b>Active</b> / <b>Inactive</b> Status for <b>{(currentHorseLodging && currentHorseLodging.horse && currentHorseLodging.horse.name) || 'Unknown'}</b>
          </Typography>

          <List
            component="div"
            disablePadding
            sx={{
              maxHeight: 250,
              overflow: 'auto',
              mt: 2,
            }}
            dense
          >
            {
              (currentHorseLodging && currentHorseLodging.id) && (
                <ListItem sx={{ alignItems: 'flex-start' }}>
                  <ListItemIcon sx={{ mt: 1 }}>
                    {currentHorseLodging.inactive
                      ? (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mx: 1,
                          }}
                        ><WrongLocation color="error" /> <Typography sx={{ fontWeight: 600 }}>INACTIVE</Typography>
                        </Box>
                      )
                      : (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mx: 1,
                          }}
                        ><LocationOn color="primary" /> <Typography sx={{ fontWeight: 600 }}>ACTIVE</Typography>
                        </Box>
                      )}
                  </ListItemIcon>
                  <TextField
                    size="small"
                    label={`${currentHorseLodging.inactive
                      ? 'Inactive'
                      : 'Active'} Status Note`}
                    fullWidth
                    disabled={isChangingStatus}
                    value={editNote}
                    helperText={`${currentHorseLodging.inactive
                      ? 'Inactive'
                      : 'Active'} since ${Moment(currentHorseLodging.startDate).format('ll')}`}
                    onChange={(e) => { setEditNote(e.target.value); }}
                  />
                  <LoadingButton
                    loading={isChangingStatus}
                    disabled={isChangingStatus}
                    sx={{
                      ml: 2,
                      mt: 0.5,
                    }}
                    size="small"
                    variant="contained"
                    onClick={handleEditNote}
                  >Save
                  </LoadingButton>
                </ListItem>
              )
            }
            {allHorseLodgings.content.length > 1 && <ListSubheader>Location and Status History</ListSubheader>}
            {
              allHorseLodgings.content.filter((hl) => hl.id !== currentHorseLodging.id)
                .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()).map((hl) => (
                  <ListItemButton dense disabled key={`past-lodging-${hl.id}`}>
                    <ListItemIcon>
                      {
                        hl.barn.id !== selectedBarn.id
                          ? (
                            <Avatar src={`https://d8adkpqcrx2wo.cloudfront.net/${hl.barn.tenant}.png`} />
                          )
                          : (
                            hl.inactive
                              ? (
                                <WrongLocation />
                              )
                              : (
                                <LocationOn />
                              )
                          )
                      }
                    </ListItemIcon>
                    <ListItemText
                      primary={`${hl.inactive
                        ? 'INACTIVE'
                        : 'ACTIVE'} ${hl.barn.name} (${Moment(hl.startDate).format('ll')} - ${hl.endDate
                          ? Moment(hl.endDate).format('ll')
                          : 'Not Ended'})`}
                      secondary={hl.note || ''}
                    />
                  </ListItemButton>
                ))
            }
          </List>
          <Divider sx={{ mt: 2 }} />
          <Box
            sx={{
              pt: 2,
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ mr: 2 }}>

              {
                (currentHorseLodging && currentHorseLodging.id && !currentHorseLodging.inactive) && (
                  <Button sx={{ ml: 2 }} startIcon={<WrongLocation />} variant="outlined" size="small" color="error" onClick={() => setOpenChangeStatusModal(true)}>Inactivate Horse</Button>
                )
              }
              {
                (currentHorseLodging && currentHorseLodging.id && currentHorseLodging.inactive) && (
                  <Button startIcon={<AddLocation />} size="small" variant="contained" onClick={() => setOpenChangeStatusModal(true)}>Make Active</Button>
                )
              }
            </Box>
            <Button size="small" onClick={() => { setOpen(false) }}>Close</Button>
          </Box>
        </Box>
      </Modal>
      <Modal
        open={openChangeStatusModal}
        onClose={() => setOpenChangeStatusModal(false)}
        aria-labelledby="modal-horse-change-status"
        aria-describedby="modal-horse-change-statusn-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: mobile
              ? 400
              : 700,
            bgcolor: 'background.paper',
            pt: 2,
            px: 4,
            pb: 3,
          }}
        >
          <Typography id="modal-horse-change-status-title" variant="h6" component="h2" gutterBottom>
            Are you sure you want to make this horse {currentHorseLodging.inactive
              ? 'ACTIVE'
              : 'INACTIVE'}
          </Typography>
          {
            !currentHorseLodging.inactive
              ? (
                <Alert variant="outlined" severity="warning" sx={{ m: 2 }}>
                  <AlertTitle>Cancel Recurring Tasks & Calendar Events, Remove From Location</AlertTitle>
                  You should first cancel all future or recurring Tasks and Calendar events for this horse. They will continue to
                  create each day until updated. Also remove the horse from any locations so their feed and other data does not show
                  on reports.
                </Alert>
              )
              : (
                <Typography>This horse will become visible again in your normal Questri screens and data.</Typography>
              )
          }
          <TextField
            sx={{
              mt: 2,
              mb: 2,
            }}
            label="Status Change Note"
            fullWidth
            disabled={isChangingStatus}
            onChange={(e) => { setNote(e.target.value); }}
            helperText="Leave a description or note about this change"
          />
          {(isChangingStatus && !changeError) && <Typography variant="caption">Please wait... this can take some time.</Typography>}
          {changeError && (
            <Alert variant="outlined" severity="error">
              <AlertTitle>Error Changing Horse Status{changeErrorMessage
                ? ''
                : ' - Contact Support'}
              </AlertTitle>
              {
                changeErrorMessage || (
                  <>
                    <Typography component="div">We were unable to change the status on this horse from your organization/barn. Please contact Questri support with the below information:</Typography>
                    <Typography variant="caption">
                      Error changing horse status for horse with ID: <b>({currentHorseLodging.horse.id})</b> and
                      boarding ID: <b>({currentHorseLodging.id})</b> from barn with ID: <b>({selectedBarn.id})</b>.
                    </Typography>
                  </>
                )
              }
            </Alert>
          )}
          <LoadingButton
            sx={{
              width: '100%',
              mt: 2,
            }}
            onClick={onChangeStatus}
            color={currentHorseLodging.inactive
              ? 'secondary'
              : 'error'}
            variant="contained"
            disabled={isFetching || isFetchingHorseLodgings || isFetchingAllHorseLodgings || isChangingStatus}
            loading={isFetching || isFetchingHorseLodgings || isFetchingAllHorseLodgings || isChangingStatus}
          >Confirm And Change Status
          </LoadingButton>
        </Box>
      </Modal>
    </List>
  );
};

LodgingsCardComponent.propTypes = {
  id: PropTypes.string.isRequired,
  defaultOpen: PropTypes.bool,
  border: PropTypes.bool,
};

LodgingsCardComponent.defaultProps = {
  defaultOpen: false,
  border: false,
}

export default LodgingsCardComponent;
